import React, { FunctionComponent, SyntheticEvent, ReactNode } from 'react'
import clsx from 'clsx'
import { warn } from '../utils/console'
import { noop } from '../utils/function'
import { isChildText } from '../utils/react'
import { Icon, isChildIcon, getIconName, getIconProps } from '../Icon'
import { Loader } from '../Loader'
import { SIZES as ICON_SIZES } from '../Icon/constants'
import { useThemeClass } from '../Theme'

import { KINDS, PADDING, SIZES, TYPES } from './constants'
import styles from './style.module.scss'

type KindsType = typeof KINDS
type KindsKeys = keyof KindsType
export type ButtonKinds = KindsType[KindsKeys]

type SizesType = typeof SIZES
type SizesKeys = keyof SizesType
export type ButtonSizes = SizesType[SizesKeys]

type PaddingType = typeof PADDING
type PaddingKeys = keyof PaddingType
export type ButtonPadding = PaddingType[PaddingKeys]

type TypesType = typeof TYPES
type TypesKeys = keyof TypesType
export type ButtonTypes = TypesType[TypesKeys]

type CSSClasses = keyof typeof styles
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  disabled?: boolean
  allowClickOnDisabled?: boolean
  kind?: ButtonKinds
  onClick?: (e: SyntheticEvent) => void
  padding?: ButtonPadding
  size?: ButtonSizes
  tabIndex?: number
  type?: ButtonTypes
  testNameSpace?: string
  loading?: boolean
  children?: ReactNode
  datadogActionName?: string
  'aria-disabled'?: boolean
  'aria-label'?: string
  'aria-labelledby'?: string
  'data-dd-action-name'?: string
}

export type ButtonVariantComponent = FunctionComponent<ButtonProps>

export type ButtonComponent = ButtonVariantComponent & {
  Primary: ButtonVariantComponent
  Secondary: ButtonVariantComponent
  Tertiary: ButtonVariantComponent
  Ghost: ButtonVariantComponent
  Link: ButtonVariantComponent
  Error: ButtonVariantComponent
  ErrorSecondary: ButtonVariantComponent
}

const BaseButton: ButtonVariantComponent = ({
  children,
  disabled = false,
  allowClickOnDisabled = false,
  kind = KINDS.PRIMARY,
  onClick = () => {},
  padding = PADDING.FIXED,
  size = SIZES.LARGE,
  tabIndex = 0,
  type = TYPES.BUTTON,
  testNameSpace = '',
  loading = false,
  ...otherProps
}) => {
  const themeClass = useThemeClass()

  if (typeof children === 'undefined') {
    return null
  }

  const datadogActionName = otherProps['data-dd-action-name']

  const getIconWarnings = (child: ReactNode): boolean => {
    const iconProps = getIconProps(child)

    if (size === SIZES.LARGE && isSquared && typeof iconProps.size !== 'undefined' && iconProps.size !== ICON_SIZES.MEDIUM) {
      warn(
        `[Core - Button] Icon contained in Button ${SIZES.LARGE} as a single child should have size="${ICON_SIZES.MEDIUM}". Icon is automatically being replaced.`
      )
      return true
    } else if (size !== SIZES.LARGE && isSquared && (typeof iconProps.size === 'undefined' || iconProps.size === ICON_SIZES.MEDIUM)) {
      warn(
        `[Core - Button] Icon contained in Button ${SIZES.REGULAR}/${SIZES.SMALL} as a single child should have size="${ICON_SIZES.SMALL} or ${ICON_SIZES.EXTRA_SMALL} or ${ICON_SIZES.MINI}". Icon is automatically being replaced.`
      )
      return true
    } else if (!isSquared && (typeof iconProps.size === 'undefined' || iconProps.size === ICON_SIZES.MEDIUM)) {
      warn(`[Core - Button] Icon contained in Button with other elements should have size="${ICON_SIZES.SMALL}". Icon is automatically being replaced.`)
      return true
    }

    return false
  }

  const isDisabled = disabled && !loading

  const hasTextChild = React.Children.toArray(children).filter((child) => isChildText(child)).length > 0
  const hasIconChild = React.Children.toArray(children).filter((child) => isChildIcon(child)).length > 0

  const isSquared = !hasTextChild && hasIconChild

  const buttonClassNames = clsx(
    styles.button,
    styles[`button${kind}` as CSSClasses],
    styles[`size${size}` as CSSClasses],
    padding === PADDING.FLUID && styles.fullWidth,
    isDisabled && styles.disabled,
    isSquared && styles[`squared${size}` as CSSClasses],
    loading && styles.loading,
    styles[`${themeClass}`]
  )
  const iconTextClassNames = clsx(styles.iconText, kind === KINDS.LINK && styles.iconTextLink)

  const onClickHandler = typeof onClick !== 'undefined' && (allowClickOnDisabled ? true : !disabled) ? onClick : noop

  const parseChildren = (): ReactNode =>
    React.Children.map(children, (child) => {
      if (isChildText(child)) {
        return <span className={iconTextClassNames}>{child}</span>
      } else if (isChildIcon(child) && getIconWarnings(child)) {
        const iconProps = getIconProps(child)
        const iconName = getIconName(child)
        const iconSize = size === SIZES.LARGE && isSquared ? ICON_SIZES.MEDIUM : ICON_SIZES.SMALL

        return <Icon {...iconProps} name={iconName} size={iconSize} />
      }

      return child
    })

  const hasLabel = typeof otherProps['aria-label'] !== 'undefined' || typeof otherProps['aria-labelledby'] !== 'undefined' || hasTextChild

  if (!hasLabel) {
    warn('[Core - Button] Component is used without any label. Please include "aria-label"/"aria-labelledby" prop or a children to meet AA compliance.')
  }

  return (
    <button
      className={buttonClassNames}
      disabled={allowClickOnDisabled ? false : disabled}
      onClick={onClickHandler}
      tabIndex={tabIndex}
      type={type}
      {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-button` })}
      {...(disabled && { 'aria-disabled': true })}
      {...otherProps}
      {...(loading && { 'aria-label': 'Loading' })}
      {...((testNameSpace.length > 0 || datadogActionName !== undefined) && { 'data-dd-action-name': `${datadogActionName ?? testNameSpace} Button` })}
    >
      {loading && (
        <div className={styles.loader}>
          <Loader pulse={false} />
        </div>
      )}
      <div aria-hidden={loading} className={styles.container}>
        {parseChildren()}
      </div>
    </button>
  )
}

const ButtonPrimary: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.PRIMARY} {...otherProps}>
      {children}
    </Button>
  )
}
const ButtonSecondary: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.SECONDARY} {...otherProps}>
      {children}
    </Button>
  )
}
const ButtonTertiary: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.TERTIARY} {...otherProps}>
      {children}
    </Button>
  )
}
const ButtonGhost: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.GHOST} {...otherProps}>
      {children}
    </Button>
  )
}

const ButtonLink: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.LINK} {...otherProps}>
      {children}
    </Button>
  )
}

const ButtonError: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.ERROR} {...otherProps}>
      {children}
    </Button>
  )
}

const ButtonErrorSecondary: ButtonVariantComponent = (props) => {
  const { children, kind, ...otherProps } = props

  return (
    <Button kind={KINDS.ERROR_SECONDARY} {...otherProps}>
      {children}
    </Button>
  )
}

export const Button = BaseButton as ButtonComponent

Button.Primary = ButtonPrimary
Button.Secondary = ButtonSecondary
Button.Tertiary = ButtonTertiary
Button.Ghost = ButtonGhost
Button.Link = ButtonLink
Button.Error = ButtonError
Button.ErrorSecondary = ButtonErrorSecondary
